import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldCheck, ShieldOff, Copy } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { Modal, Button, Field, Input, Badge, Spinner } from '../../ui';

const codeInput = (value) => value.replace(/\D/g, '').slice(0, 6);

/** Двухфакторный вход: включение по QR-коду и отключение кодом из приложения */
export default function TwoFactorDialog({ open, onClose }) {
  const { t } = useI18n();
  const { admin, reload } = useSession();
  const enabled = admin?.totp_enabled === 1;
  const [setup, setSetup] = useState(null); // { qr, secret }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setSetup(null); setCode(''); }
  }, [open]);

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/2fa/setup');
      setSetup(res.data);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(enabled ? '/admin/2fa/disable' : '/admin/2fa/enable', { code });
      toast.success(enabled ? t('twofa.disabled') : t('twofa.enabled'));
      await reload();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(setup.secret); toast.success(t('home.copied')); } catch { /* буфер недоступен */ }
  };

  const codeField = (
    <Field label={t('login.codeLabel')}>
      <Input
        value={code}
        onChange={(e) => setCode(codeInput(e.target.value))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        required
        autoFocus
        className="h-11 text-center font-mono text-lg tracking-[0.4em]"
      />
    </Field>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('twofa.title')}
      description={t('twofa.subtitle')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
          {(enabled || setup) && (
            <Button type="submit" form="twofa-form" loading={busy} disabled={code.length !== 6} variant={enabled ? 'danger' : 'primary'}>
              {enabled ? t('twofa.disable') : t('twofa.confirm')}
            </Button>
          )}
        </>
      }
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm text-zinc-600 dark:text-zinc-400">{t('twofa.status')}:</span>
        <Badge tone={enabled ? 'success' : 'neutral'} dot>{enabled ? t('twofa.on') : t('twofa.off')}</Badge>
      </div>

      {enabled ? (
        <form id="twofa-form" onSubmit={submit} className="space-y-4">
          <p className="flex items-start gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />{t('twofa.disableHint')}
          </p>
          {codeField}
        </form>
      ) : !setup ? (
        <div className="space-y-4">
          <p className="flex items-start gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />{t('twofa.intro')}
          </p>
          <Button onClick={start} loading={busy} icon={ShieldCheck} className="w-full justify-center">{t('twofa.start')}</Button>
        </div>
      ) : (
        <form id="twofa-form" onSubmit={submit} className="space-y-4">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-zinc-600 dark:text-zinc-400">
            <li>{t('twofa.step1')}</li>
            <li>{t('twofa.step2')}</li>
          </ol>
          <div className="flex justify-center">
            {setup.qr ? <img src={setup.qr} alt={t('twofa.qrAlt')} className="h-44 w-44 rounded-lg border border-zinc-200 bg-white p-1 dark:border-zinc-700" /> : <Spinner />}
          </div>
          <div className="flex items-center justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2 dark:bg-zinc-800/60">
            <code className="break-all font-mono text-xs text-zinc-700 dark:text-zinc-300">{setup.secret}</code>
            <Button variant="ghost" size="icon-sm" icon={Copy} onClick={copySecret} aria-label={t('home.copy')} />
          </div>
          {codeField}
        </form>
      )}
    </Modal>
  );
}
